/**
 * OwnershipManager Component
 * Allows main owners and admins to manage shared access to content
 * Provides UI for adding/removing shared users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Button, 
  List, 
  Avatar, 
  Select, 
  Space, 
  Typography, 
  Tag, 
  Popconfirm,
  Spin,
  Empty,
  message,
  Divider
} from 'antd';
import { 
  UserOutlined, 
  UserAddOutlined, 
  DeleteOutlined,
  CrownOutlined,
  TeamOutlined,
  SearchOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styles from './OwnershipManager.module.css';

const { Text, Title } = Typography;

/**
 * Ownership manager modal for sharing/revoking access
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether modal is visible
 * @param {Function} props.onClose - Close callback
 * @param {Object} props.ownershipInfo - Ownership information from useOwnership
 * @param {Function} props.onShare - Share callback (userId) => Promise
 * @param {Function} props.onRevoke - Revoke callback (userId) => Promise
 * @param {Function} props.onGetAvailableUsers - Get available users callback (search?) => Promise<users[]>
 * @param {Array} props.availableUsers - List of users available to share with
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.loadingAvailableUsers - Loading available users state
 * @param {boolean} props.canManageOwnership - Whether current user can manage
 */
export function OwnershipManager({
  open,
  onClose,
  ownershipInfo,
  onShare,
  onRevoke,
  onGetAvailableUsers,
  availableUsers = [],
  loading = false,
  loadingAvailableUsers = false,
  canManageOwnership = false,
}) {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [revoking, setRevoking] = useState(null);

  // Load available users when modal opens
  useEffect(() => {
    if (open && canManageOwnership) {
      onGetAvailableUsers?.();
    }
  }, [open, canManageOwnership, onGetAvailableUsers]);

  // Handle search
  const handleSearch = useCallback((value) => {
    onGetAvailableUsers?.(value || null);
  }, [onGetAvailableUsers]);

  // Handle share
  const handleShare = useCallback(async () => {
    if (!selectedUserId) return;
    
    setSharing(true);
    try {
      const success = await onShare?.(selectedUserId);
      if (success) {
        message.success(t('ownership.shareSuccess'));
        setSelectedUserId(null);
      }
    } finally {
      setSharing(false);
    }
  }, [selectedUserId, onShare, t]);

  // Handle revoke
  const handleRevoke = useCallback(async (userId) => {
    setRevoking(userId);
    try {
      const success = await onRevoke?.(userId);
      if (success) {
        message.success(t('ownership.revokeSuccess'));
      }
    } finally {
      setRevoking(null);
    }
  }, [onRevoke, t]);

  const mainOwner = ownershipInfo?.mainOwner;
  const sharedOwners = ownershipInfo?.sharedOwners || [];

  // Format user options for select
  const userOptions = availableUsers.map(user => ({
    value: user.id,
    label: (
      <Space>
        <Avatar size="small" icon={<UserOutlined />} />
        <span>{user.displayName}</span>
        <Text type="secondary" style={{ fontSize: '12px' }}>{user.email}</Text>
      </Space>
    ),
    searchText: `${user.displayName} ${user.email}`.toLowerCase(),
  }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <TeamOutlined />
          {t('ownership.manageTitle')}
        </Space>
      }
      footer={[
        <Button key="close" onClick={onClose}>
          {t('common.close')}
        </Button>
      ]}
      width={600}
      className={styles.modal}
    >
      <Spin spinning={loading}>
        {/* Main Owner Section */}
        <div className={styles.section}>
          <Title level={5}>
            <CrownOutlined className={styles.sectionIcon} />
            {t('ownership.mainOwner')}
          </Title>
          {mainOwner ? (
            <div className={styles.ownerCard}>
              <Avatar size="large" icon={<UserOutlined />} />
              <div className={styles.ownerInfo}>
                <Text strong>{mainOwner.displayName}</Text>
                <Text type="secondary">{mainOwner.email}</Text>
              </div>
              <Tag color="gold">{t('ownership.owner')}</Tag>
            </div>
          ) : (
            <Empty 
              description={t('ownership.noOwner')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
            />
          )}
        </div>

        <Divider />

        {/* Shared Users Section */}
        <div className={styles.section}>
          <Title level={5}>
            <TeamOutlined className={styles.sectionIcon} />
            {t('ownership.sharedWith')} ({sharedOwners.length})
          </Title>
          
          {sharedOwners.length > 0 ? (
            <List
              className={styles.sharedList}
              dataSource={sharedOwners}
              renderItem={(item) => (
                <List.Item
                  actions={canManageOwnership ? [
                    <Popconfirm
                      key="revoke"
                      title={t('ownership.revokeConfirmTitle')}
                      description={t('ownership.revokeConfirmDesc', { name: item.user.displayName })}
                      onConfirm={() => handleRevoke(item.user.id)}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                      okType="danger"
                    >
                      <Button 
                        type="text" 
                        danger 
                        icon={<DeleteOutlined />}
                        loading={revoking === item.user.id}
                        size="small"
                      >
                        {t('ownership.revoke')}
                      </Button>
                    </Popconfirm>
                  ] : []}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={item.user.displayName}
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{item.user.email}</Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {t('ownership.sharedOn', { 
                            date: new Date(item.dateShared).toLocaleDateString(),
                            by: item.sharedBy?.displayName || t('ownership.unknown')
                          })}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty 
              description={t('ownership.noSharedUsers')} 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </div>

        {/* Add User Section - only for main owner/admin */}
        {canManageOwnership && (
          <>
            <Divider />
            <div className={styles.section}>
              <Title level={5}>
                <UserAddOutlined className={styles.sectionIcon} />
                {t('ownership.addUser')}
              </Title>
              <div className={styles.addUserForm}>
                <Select
                  className={styles.userSelect}
                  placeholder={t('ownership.searchUsers')}
                  showSearch
                  allowClear
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                  onSearch={handleSearch}
                  loading={loadingAvailableUsers}
                  options={userOptions}
                  filterOption={(input, option) => 
                    option.searchText.includes(input.toLowerCase())
                  }
                  notFoundContent={
                    loadingAvailableUsers ? (
                      <Spin size="small" />
                    ) : (
                      <Empty 
                        description={t('ownership.noUsersFound')} 
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )
                  }
                  suffixIcon={<SearchOutlined />}
                />
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={handleShare}
                  loading={sharing}
                  disabled={!selectedUserId}
                >
                  {t('ownership.share')}
                </Button>
              </div>
              <Text type="secondary" className={styles.hint}>
                {t('ownership.shareHint')}
              </Text>
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
}

OwnershipManager.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ownershipInfo: PropTypes.shape({
    mainOwner: PropTypes.object,
    sharedOwners: PropTypes.array,
  }),
  onShare: PropTypes.func,
  onRevoke: PropTypes.func,
  onGetAvailableUsers: PropTypes.func,
  availableUsers: PropTypes.array,
  loading: PropTypes.bool,
  loadingAvailableUsers: PropTypes.bool,
  canManageOwnership: PropTypes.bool,
};

export default OwnershipManager;
